import React, { useState, useRef, useCallback, useEffect } from 'react'
import { useAtom } from 'jotai'
import { motion, AnimatePresence } from 'framer-motion'
import ActualPapa, { type ParseResult, type ParseConfig } from 'papaparse'

import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import SlidingToggle from '@/components/ui/SlidingToggle'
import { 
  Upload,
  FileSpreadsheet,
  Clipboard,
  X,
  Database
} from 'lucide-react'
import { 
  addChartAtom,
  type ChartData,
  type DataRow,
  createStandardChartObject
} from '../../store/chartAtoms'
import DataSourceSelector from '../datasources/DataSourceSelector'
import { useToast } from '@/hooks/use-toast'
import { parseChatGptCostLog } from '@/lib/messyDataParser'

interface QuickChartCreatorProps {
  isVisible: boolean
  onClose: () => void
  onSuccess?: () => void
  _papaParseOverride?: (input: string, config?: ParseConfig) => ParseResult<DataRow>;
}

const QuickChartCreator: React.FC<QuickChartCreatorProps> = ({ 
  isVisible, 
  onClose, 
  onSuccess, 
  _papaParseOverride
}) => {
  const [, addChart] = useAtom(addChartAtom)
  const [currentMode, setCurrentMode] = useState<'upload' | 'paste' | 'dataSource'>('upload')
  const [pasteData, setPasteData] = useState('')
  const [isDragging, setIsDragging] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { toast } = useToast()

  useEffect(() => {
    if (isVisible) {
      // console.log('[QuickCreator] Received defaultPaletteInfo:', defaultPaletteInfo);
    }
  }, [isVisible]);

  const analyzeData = useCallback((data: DataRow[], title?: string): Omit<ChartData, 'id' | 'createdAt'> => {
    if (data.length === 0) throw new Error('No data provided')
    
    const columns = Object.keys(data[0])
    if (columns.length < 2) throw new Error('Need at least 2 columns for a chart')

    const numericColumns = columns.filter(col => 
      data.every(row => !isNaN(Number(row[col])) && row[col] !== '')
    )
    const textColumns = columns.filter(col => 
      !numericColumns.includes(col)
    )
    
    let chartType: 'bar' | 'line' | 'pie' = 'bar'
    let xAxis = textColumns[0] || columns[0]
    let yAxis = numericColumns[0] || columns[1]
    
    const isTimeBasedData = textColumns.some(col => 
      col.toLowerCase().includes('date') || 
      col.toLowerCase().includes('time') ||
      col.toLowerCase().includes('month') ||
      col.toLowerCase().includes('year') ||
      col.toLowerCase().includes('timestamp')
    ) || (xAxis && data.some(row => {
      const value = row[xAxis]
      return typeof value === 'string' && (
        /\d{4}-\d{2}-\d{2}/.test(value) ||
        /\d{2}\/\d{2}\/\d{4}/.test(value) || 
        /\d{1,2}:\d{2}/.test(value) || 
        /\w{3}\s+\d{1,2},?\s+\d{4}/.test(value) ||
        /\w{3}\s+\d{4}/.test(value)
      )
    }))
    if (data.length <= 10 && numericColumns.length === 1) {
      chartType = 'pie'
    } else if (isTimeBasedData) {
      chartType = 'line'
    }
    
    let processedData = data.map(row => {
      const newRow: DataRow = { ...row }
      numericColumns.forEach(col => {
        newRow[col] = Number(row[col])
      })
      return newRow
    })
    if (isTimeBasedData && xAxis) {
      processedData = processedData.sort((a, b) => {
        const aValue = a[xAxis]
        const bValue = b[xAxis]
        
        // Try to parse dates, with special handling for "Month YYYY" format
        let aDate = new Date(aValue)
        let bDate = new Date(bValue)
        
        // If standard parsing fails, try "Month YYYY" format
        if (isNaN(aDate.getTime()) && typeof aValue === 'string' && /\w{3}\s+\d{4}/.test(aValue)) {
          aDate = new Date(aValue + " 1") // Add day 1 to make it parseable
        }
        if (isNaN(bDate.getTime()) && typeof bValue === 'string' && /\w{3}\s+\d{4}/.test(bValue)) {
          bDate = new Date(bValue + " 1") // Add day 1 to make it parseable  
        }
        
        if (!isNaN(aDate.getTime()) && !isNaN(bDate.getTime())) {
          return aDate.getTime() - bDate.getTime()
        }
        return String(aValue).localeCompare(String(bValue))
      })
    }

    // Use the new factory to create a standardized chart object
    const newChartObject = createStandardChartObject({
      data: processedData,
      columns,
      chartType,
      xAxis,
      yAxis,
      title: title || 'Untitled Chart',
    });

    return newChartObject;
  }, [])

  const processCSV = useCallback((csvText: string, fileTitle?: string) => {
    setLoading(true)
    setError('')
    
    const finalizeSuccess = (analyzedChart: Omit<ChartData, 'id' | 'createdAt'>) => {
      addChart(analyzedChart)

      setError('')
      setPasteData('')
      setCurrentMode('upload')
      onClose()
      onSuccess?.()
    }
    
    try {
      // 1️⃣ Attempt specialised messy-log parsing first
      const messyParsed = parseChatGptCostLog(csvText)
      if (messyParsed) {
        const analyzed = analyzeData(messyParsed.rows, messyParsed.title)
        finalizeSuccess(analyzed)
        toast({ title: 'Log data detected', description: 'We automatically extracted the timestamp & cost values.' })
        return
      }

      // 2️⃣ Fallback to generic CSV parsing
      const config: ParseConfig = {
        header: true,
        skipEmptyLines: true,
        transformHeader: (header: string) => header.trim(),
      }

      const result = _papaParseOverride
        ? _papaParseOverride(csvText, config)
        : ActualPapa.parse<DataRow>(csvText, config)

      const hasErrors = result.errors.length > 0
      const hasInsufficientData = result.data.length === 0 || Object.keys(result.data[0] || {}).length < 2

      if (hasErrors || hasInsufficientData) {
        throw new Error(hasErrors ? result.errors[0].message : 'No tabular structure detected')
      }

      const chartTitle = fileTitle || (result.data.length > 0 ? 'Chart from CSV' : 'Untitled Chart')
      const analyzed = analyzeData(result.data, chartTitle)
      finalizeSuccess(analyzed)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to process data')
    } finally {
      setLoading(false)
    }
  }, [addChart, onClose, onSuccess, _papaParseOverride, analyzeData, toast])

  const handleFileUpload = (file: File) => {
    if (!file.name.endsWith('.csv')) {
      setError('Please upload a CSV file')
      return
    }
    const fileTitle = file.name.replace(/\.csv$/i, '');

    const reader = new FileReader()
    reader.onload = (e) => {
      const csvText = e.target?.result as string
      processCSV(csvText, fileTitle)
    }
    reader.readAsText(file)
  }

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
    
    const files = Array.from(e.dataTransfer.files)
    if (files.length > 0) {
      handleFileUpload(files[0])
    }
  }, [])

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
  }, [])

  const handlePaste = () => {
    if (pasteData.trim()) {
      processCSV(pasteData)
    }
  }

  const handleClose = () => {
    setError('')
    setPasteData('')
    setCurrentMode('upload')
    setIsDragging(false)
    onClose()
  }

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.4, ease: "easeInOut" }}
          className="overflow-hidden"
        >
          <motion.div
            initial={{ y: -20 }}
            animate={{ y: 0 }}
            exit={{ y: -20 }}
            transition={{ duration: 0.3, delay: 0.1 }}
            className="space-y-6 py-6"
          >
            {error && (
              <motion.div 
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="rounded-md bg-destructive/10 border border-destructive/20 p-3"
              >
                <p className="text-sm text-destructive">{error}</p>
              </motion.div>
            )}

            {/* Mode Toggle with Close Button */}
            <div className="flex justify-center items-center relative">
              <SlidingToggle
                options={[
                  {
                    value: 'upload',
                    label: 'Upload File',
                    icon: <FileSpreadsheet className="h-4 w-4" />
                  },
                  {
                    value: 'paste',
                    label: 'Paste Data',
                    icon: <Clipboard className="h-4 w-4" />
                  },
                  {
                    value: 'dataSource',
                    label: 'Data Source',
                    icon: <Database className="h-4 w-4" />
                  }
                ]}
                value={currentMode}
                onChange={(value) => setCurrentMode(value as 'upload' | 'paste' | 'dataSource')}
                className="w-96"
              />
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleClose}
                className="absolute -right-12 text-muted-foreground hover:text-foreground"
                aria-label="Close quick chart creator"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            {/* Content Area with Smooth Transitions */}
            <div className="min-h-[240px]">
              <AnimatePresence mode="wait">
                {currentMode === 'upload' ? (
                  /* File Upload Mode */
                  <motion.div
                    key="upload"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    transition={{ duration: 0.3, ease: "easeInOut" }}
                    className={`relative rounded-lg border-2 border-dashed p-6 text-center transition-colors ${
                      isDragging 
                        ? 'border-primary bg-primary/5' 
                        : 'border-gray-300 hover:border-primary/50'
                    }`}
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    aria-label="CSV file upload area"
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".csv"
                      onChange={(e) => e.target.files?.[0] && handleFileUpload(e.target.files[0])}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                      aria-labelledby="file-upload-label"
                    />
                    <motion.div
                      initial={{ scale: 0.8, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ delay: 0.1, duration: 0.3 }}
                    >
                      <FileSpreadsheet className="mx-auto h-10 w-10 text-gray-400 mb-3" />
                      <p id="file-upload-label" className="text-base font-medium text-gray-900 mb-1">
                        Drop your CSV file here
                      </p>
                      <p className="text-sm text-gray-500 mb-3">
                        or click to browse files
                      </p>
                      <Button variant="outline" size="sm" className="pointer-events-none">
                        <Upload className="h-4 w-4 mr-2" />
                        Choose File
                      </Button>
                    </motion.div>
                  </motion.div>
                ) : (
                  /* Paste Mode */
                  <motion.div
                    key="paste"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.3, ease: "easeInOut" }}
                    className="space-y-4"
                  >
                    <motion.div
                      initial={{ y: 10, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ delay: 0.1, duration: 0.3 }}
                      className="flex items-center justify-between"
                    >
                      <div className="flex items-center gap-2">
                        <Clipboard className="h-4 w-4 text-gray-500" />
                        <Label htmlFor="paste-area" className="text-sm font-medium text-gray-700">Paste your CSV data</Label>
                      </div>
                      <Button
                        onClick={async () => {
                          try {
                            const clipboardText = await navigator.clipboard.readText()
                            setPasteData(clipboardText)
                          } catch (err) {
                            console.error('Failed to read clipboard:', err)
                            setError('Failed to read clipboard. Please paste manually.')
                          }
                        }}
                        className="bg-orange-500 hover:bg-orange-600 text-white"
                        size="sm"
                      >
                        <Clipboard className="h-4 w-4 mr-2" />
                        Paste Data
                      </Button>
                    </motion.div>
                    <motion.div
                      initial={{ y: 20, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ delay: 0.2, duration: 0.3 }}
                    >
                      <Textarea
                        id="paste-area"
                        placeholder="Paste CSV data here... (e.g., Name,Value&#10;Product A,100&#10;Product B,150)"
                        value={pasteData}
                        onChange={(e) => setPasteData(e.target.value)}
                        rows={5}
                        className="font-mono text-sm"
                      />
                    </motion.div>
                    <motion.div
                      initial={{ y: 20, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ delay: 0.3, duration: 0.3 }}
                    >
                      <Button 
                        onClick={handlePaste} 
                        disabled={!pasteData.trim() || loading}
                        className="w-full"
                      >
                        {loading ? 'Creating Chart...' : 'Create Chart'}
                      </Button>
                    </motion.div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Quick Examples */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.3 }}
              className="text-center border-t pt-3"
            >
              <p className="text-xs text-gray-500 mb-2">Need sample data? Try these:</p>
              <div className="flex gap-2 justify-center">
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <Button
                    size="sm"
                    className="text-muted-foreground bg-gray-100 dark:bg-gray-700 hover:text-foreground dark:text-gray-400 dark:hover:text-gray-200"
                    onClick={() => {
                      const sampleData = 'Month,Sales\nMay 2025,4000\nApr 2025,5000\nMar 2025,3000\nFeb 2025,5900\nJan 2025,5800\nDec 2024,3500\nNov 2024,4200\nOct 2024,3800\nSep 2024,4500\nAug 2024,5100\nJul 2024,4800\nJun 2024,4200\nMay 2024,3900\nApr 2024,4400\nMar 2024,3600\nFeb 2024,3100\nJan 2024,2800\nDec 2023,3200\nNov 2023,3800\nOct 2023,4200\nSep 2023,4600\nAug 2023,4900\nJul 2023,4500\nJun 2023,4100'
                      setPasteData(sampleData)
                      processCSV(sampleData, 'Sales Data')
                    }}
                  >
                    Sales Data
                  </Button>
                </motion.div>
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <Button
                    size="sm"
                    className="text-muted-foreground bg-gray-100 dark:bg-gray-700 hover:text-foreground dark:text-gray-400 dark:hover:text-gray-200"
                    onClick={() => {
                      const sampleData = 'Category,Count\nDesktop,45\nMobile,55\nTablet,20\nLaptop,65\nSmartphone,75\niPad,30\nChromebook,25\nSurface,40\nMacBook,60\nWindows PC,50\nAndroid,70\niOS,55\nLinux,35\nChrome OS,45\nFirefox OS,15\nUbuntu,20\nFedora,25\nDebian,30\nMint,35\nArch,40'
                      setPasteData(sampleData)
                      processCSV(sampleData, 'Usage Stats')
                    }}
                  >
                    Usage Stats
                  </Button>
                </motion.div>
              </div>
            </motion.div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export default QuickChartCreator 